import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Scrollbars } from "react-custom-scrollbars-2";

import {
  QuicktalesInOrgTableHeadCells,
  getComparator,
  stableSort,
  routes,
} from "../../../../constants";
import { TableRowSection } from "../components";
import TablePaginationSection from "../../../../common/TablePaginationSection";
import ImagePreviewModal from "../../../../common/ImagePreviewModal";
import AudioModal from "../../../../common/AudioModal";
import { useQuickTalesContext } from "../../../../providers/OrganizationAdmin/QuickTalesProvider";
import CommonTableHead from "../../../../common/CommonTableHead";
import { mainBoxContainerStyles, tablePaperContainerStyles } from "./styles";

export default function QuickTalesTable({
  selected,
  setSelected,
  quictalesInOrg,
  onRowClick, // Optional custom navigation handler
  tableHeadCells = QuicktalesInOrgTableHeadCells,
  RowComponent = TableRowSection,
  rowProps = {}, // Optional extra props to pass into each RowComponent
  defaultOrder = "desc",
  defaultOrderBy = "approvalStatus.updatedAt",
  defaultSecondaryOrderBy = null,
  isRowSelectable = () => true,
}) {
  const {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useQuickTalesContext();
  const navigate = useNavigate();
  const scrollbarsRef = useRef(null);

  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);

  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Initialize column widths based on QuicktalesInOrgTableHeadCells
  const initializeColumnWidths = () => {
    const widths = {};
    tableHeadCells.forEach((cell) => {
      widths[cell.id] = cell.width || 150;
    });
    return widths;
  };

  const [columnWidths, setColumnWidths] = useState(initializeColumnWidths);

  const handleColumnResize = (columnId, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth
    }));
  };

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
    setSelectedImage(null);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
    setSelectedAudioFile(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page - 1 > 0
      ? Math.max(0, (1 + page - 1) * rowsPerPage - quictalesInOrg.length)
      : 0;

  const visibleRows = useMemo(
    () => {
      const primary = getComparator(order, orderBy);
      const secondary = defaultSecondaryOrderBy
        ? // If the primary compare ties, fall back to the secondary compare.
          // We keep secondary direction aligned with primary direction.
          getComparator(order, defaultSecondaryOrderBy)
        : null;

      const combinedComparator = secondary
        ? (a, b) => {
            const res = primary(a, b);
            if (res !== 0) return res;
            return secondary(a, b);
          }
        : primary;

      return stableSort(quictalesInOrg, combinedComparator).slice(
        (page - 1) * rowsPerPage,
        (page - 1) * rowsPerPage + rowsPerPage
      );
    },
    [order, orderBy, page, rowsPerPage, quictalesInOrg, defaultSecondaryOrderBy]
  );

  const selectableVisibleRows = useMemo(() => {
    return visibleRows.filter((row) => {
      try {
        return isRowSelectable(row);
      } catch (e) {
        // If consumer predicate throws, fail open to keep UX usable.
        return true;
      }
    });
  }, [visibleRows, isRowSelectable]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = selectableVisibleRows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClickRow = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // console.log(Math.ceil(2.1))

  return (
    <Box sx={mainBoxContainerStyles}>
      {/* <Scrollbars
        style={scrollbar}
        autoHide={true}
        ref={scrollbarsRef} // Assign the ref to Scrollbars instance
        renderThumbVertical={({ style, ...props }) => (
          <div
            {...props}
            style={{
              ...style,
              ...scrollbarprops,
            }}
          />
        )}
      > */}
      <Paper sx={tablePaperContainerStyles}>
        <TableContainer
          style={{ border: "none", boxShadow: "none", maxHeight: "calc(90vh - 220px)", maxWidth: "calc(90vw - 120px)" }}
        >
          <Table
            stickyHeader
            aria-labelledby="tableTitle"
          >
            <CommonTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={selectableVisibleRows.length}
              tableHeadCells={tableHeadCells}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
            />
            {/* <TableHeadSection
      >
        <Paper sx={tablePaperContainerStyles}>
          <TableContainer style={{ border: "none", boxShadow: "none" }}>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
              <TableHeadComponent
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                // rowCount={quictalesInOrg.length}
                rowCount={visibleRows.length}
              /> */}

            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                const numberType = index % 2 == 0;

                return (
                  <RowComponent
                    isItemSelected={isItemSelected}
                    row={row}
                    handleClickRow={handleClickRow}
                    numberType={numberType}
                    labelId={labelId}
                    key={index}
                    columnWidths={columnWidths}
                    handleImagePreviewModalOpen={handleImagePreviewModalOpen}
                    handleAudioModalOpen={handleAudioModalOpen}
                    setSelectedAudioFile={setSelectedAudioFile}
                    setSelectedImage={setSelectedImage}
                    handleNavigateToDetails={() => {
                      // Use custom handler if provided, otherwise use default navigation
                      if (onRowClick) {
                        onRowClick(row);
                      } else {
                        navigate(routes.quicktaleInOrgDetails, {
                          state: { quicktaleDetails: row },
                        });
                      }
                    }}
                    {...rowProps}
                  />
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
               {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No results found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* </Scrollbars> */}
      <TablePaginationSection
        count={Math.ceil(quictalesInOrg.length / rowsPerPage)}
        // count={Math.ceil(quictalesInOrg.length / rowsPerPage)}
        page={page}
        total={quictalesInOrg.length ?? 0}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={selectedImage && selectedImage}
        // image={images.tableDummyImage}
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={selectedAudioFile && selectedAudioFile}
        // audioFile={audios.audioSample}
      />
    </Box>
  );
}
